import { ButtonHTMLAttributes, forwardRef } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({ variant = 'primary', size = 'md', isLoading, className = '', children, disabled, ...props }, ref) => {
		const baseClasses = 'font-semibold rounded-lg border-none cursor-pointer transition-all duration-200 inline-flex items-center justify-center gap-2';

		const variantClasses = {
			primary: 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 dark:bg-blue-500 dark:hover:bg-blue-600 dark:disabled:bg-gray-600',
			secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 dark:disabled:bg-gray-800',
			danger: 'bg-white text-red-600 border border-red-200 hover:bg-red-50 disabled:bg-gray-100 dark:bg-gray-700 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20 dark:disabled:bg-gray-800',
			success: 'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 dark:bg-green-500 dark:hover:bg-green-600 dark:disabled:bg-gray-600',
		};

		const sizeClasses = {
			sm: 'px-3 py-1.5 text-sm',
			md: 'px-4 py-2.5 text-sm',
			lg: 'px-6 py-3.5 text-base',
		};

		return (
			<button
				ref={ref}
				className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
				disabled={disabled || isLoading}
				{...props}
			>
				{isLoading && (
					<svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						></path>
					</svg>
				)}
				{children}
			</button>
		);
	},
);

Button.displayName = 'Button';

export default Button;
